import React, { useEffect, useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash, 
  Calendar,
  Users,
  Package,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface Product {
  id: number;
  nom: string;
}

interface Promotion {
  id: number;
  nom: string;
  product_id: number;
  date_debut: string;
  date_fin: string;
  is_active: boolean;
  created_at: string;
  product: Product;
  users_count: number;
}

interface PromotionFormData {
  nom: string;
  product_id: number;
  date_debut: string;
  date_fin: string;
  is_active: boolean;
}

export default function FormateurPromotions() {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);
  const [formData, setFormData] = useState<PromotionFormData>({
    nom: '',
    product_id: 0,
    date_debut: '',
    date_fin: '',
    is_active: true
  });

  useEffect(() => {
    fetchPromotions();
    fetchProducts();
  }, []);

  const fetchPromotions = async () => {
    try {
      const response = await api.get('/formateur/promotions');
      setPromotions(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des promotions');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/formateur/products');
      setProducts(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des produits');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      ...formData,
      date_debut: new Date(formData.date_debut).toISOString(),
      date_fin: new Date(formData.date_fin).toISOString(),
    };

    try {
      if (editingPromotion) {
        await api.put(`/formateur/promotions/${editingPromotion.id}`, payload);
        toast.success('Promotion modifiée avec succès');
      } else {
        await api.post('/formateur/promotions', payload);
        toast.success('Promotion créée avec succès');
      }

      setShowModal(false);
      setEditingPromotion(null);
      resetForm();
      fetchPromotions();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette promotion ?')) return;

    try {
      await api.delete(`/formateur/promotions/${id}`);
      toast.success('Promotion supprimée');
      fetchPromotions();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const openEditModal = (promotion: Promotion) => {
    setEditingPromotion(promotion);
    setFormData({
      nom: promotion.nom,
      product_id: promotion.product_id,
      date_debut: promotion.date_debut.split('T')[0],
      date_fin: promotion.date_fin.split('T')[0],
      is_active: promotion.is_active
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      product_id: products[0]?.id || 0,
      date_debut: '',
      date_fin: '',
      is_active: true
    });
  };

  const getPromotionStatus = (promotion: Promotion) => {
    const now = new Date();
    const start = new Date(promotion.date_debut);
    const end = new Date(promotion.date_fin);

    if (!promotion.is_active) {
      return { status: 'inactive', label: 'Inactive', color: 'bg-gray-100 text-gray-800' };
    }

    if (now < start) {
      return { status: 'future', label: 'À venir', color: 'bg-blue-100 text-blue-800' };
    }

    if (now > end) {
      return { status: 'ended', label: 'Terminée', color: 'bg-red-100 text-red-800' };
    }

    return { status: 'active', label: 'Active', color: 'bg-green-100 text-green-800' };
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des promotions</h1>
          <p className="mt-1 text-gray-600">
            {promotions.length} promotion{promotions.length > 1 ? 's' : ''} au total
          </p>
        </div>
        <button
          onClick={() => {
            setEditingPromotion(null);
            resetForm();
            setShowModal(true);
          }}
          className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nouvelle promotion
        </button>
      </div>

      {/* Statistiques rapides */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{promotions.length}</p>
            </div>
            <Calendar className="h-8 w-8 text-gray-400" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Actives</p>
              <p className="text-2xl font-bold text-green-600">
                {promotions.filter(p => getPromotionStatus(p).status === 'active').length}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">À venir</p>
              <p className="text-2xl font-bold text-blue-600">
                {promotions.filter(p => getPromotionStatus(p).status === 'future').length}
              </p>
            </div>
            <Clock className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Terminées</p>
              <p className="text-2xl font-bold text-gray-600">
                {promotions.filter(p => getPromotionStatus(p).status === 'ended').length}
              </p>
            </div>
            <XCircle className="h-8 w-8 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Liste des promotions : Desktop (tableau) */}
      <div className="hidden md:block bg-white shadow rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Promotion
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Produit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Période
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Inscrits
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {promotions.map((promotion) => {
                const status = getPromotionStatus(promotion);
                return (
                  <tr key={promotion.id}>
                    <td className="px-6 py-4 whitespace-nowrap">{promotion.nom}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{promotion.product?.nom}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {formatDate(promotion.date_debut)} - {formatDate(promotion.date_fin)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{promotion.users_count}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${status.color}`}
                      >
                        {status.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        onClick={() => openEditModal(promotion)}
                        className="text-indigo-600 hover:text-indigo-900"
                        aria-label="Modifier"
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(promotion.id)}
                        className="text-red-600 hover:text-red-900"
                        aria-label="Supprimer"
                      >
                        <Trash className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Liste des promotions : Mobile (cartes) */}
      <div className="md:hidden space-y-3">
        {promotions.map((promotion) => {
          const status = getPromotionStatus(promotion);
          return (
            <div key={promotion.id} className="bg-white shadow rounded-lg p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-gray-900 break-words">
                    {promotion.nom}
                  </h3>
                  <div className="mt-1 text-sm text-gray-600 flex items-center gap-2">
                    <Package className="h-4 w-4 text-gray-400" />
                    <span className="break-words">{promotion.product?.nom}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEditModal(promotion)}
                    className="p-2 rounded-md text-primary hover:bg-blue-50"
                    aria-label="Modifier"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(promotion.id)}
                    className="p-2 rounded-md text-red-600 hover:bg-red-50"
                    aria-label="Supprimer"
                  >
                    <Trash className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-gray-600">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                  {formatDate(promotion.date_debut)} - {formatDate(promotion.date_fin)}
                </div>
                <div className="flex items-center">
                  <Users className="h-4 w-4 mr-1 text-gray-400" />
                  {promotion.users_count} inscrits
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                  {status.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal formulaire */}
      {showModal && (
        <div className="modal-backdrop fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/40">
          <div className="w-full max-w-2xl bg-white rounded-xl shadow-xl p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">
              {editingPromotion ? 'Modifier la promotion' : 'Nouvelle promotion'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nom</label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  required
                  className="mt-1.5 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-primary focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Produit</label>
                <select
                  name="product_id"
                  value={formData.product_id}
                  onChange={(e) => setFormData({ ...formData, product_id: parseInt(e.target.value) })}
                  required
                  className="mt-1.5 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-primary focus:border-primary"
                >
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.nom}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Date début</label>
                <input
                  type="date"
                  name="date_debut"
                  value={formData.date_debut}
                  onChange={(e) => setFormData({ ...formData, date_debut: e.target.value })}
                  required
                  className="mt-1.5 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-primary focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Date fin</label>
                <input
                  type="date"
                  name="date_fin"
                  value={formData.date_fin}
                  onChange={(e) => setFormData({ ...formData, date_fin: e.target.value })}
                  required
                  className="mt-1.5 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-primary focus:border-primary"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="mr-2"
                />
                <label className="text-sm font-medium text-gray-700">Active</label>
              </div>

              {/* Actions compactes */}
              <div className="mt-6 sm:mt-8 sticky bottom-0 bg-white pb-2 pt-3">
                <div className="flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingPromotion(null);
                      resetForm();
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
                  >
                    {editingPromotion ? 'Modifier' : 'Créer'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}